/**
 * useTaskSiblings — Resolve a task's position within its workflow instance.
 *
 * Takes the full task list (already loaded by WorkflowStateView) and filters
 * to siblings sharing the same workflowInstanceId. No new GraphQL query needed.
 *
 * DUMB FRONTEND: Data comes from the parent's already-fetched task array.
 */

import { useMemo } from 'react';
import { GqlTaskStatus, type TaskFieldsFragment } from '@/generated/graphql';

export interface TaskSiblingInfo {
  /** Total tasks in this workflow instance */
  total: number;
  /** 1-based position of the current task (by sequenceOrder or creation order) */
  position: number;
  /** All sibling tasks sorted by sequenceOrder */
  siblings: TaskFieldsFragment[];
  /** Count of completed siblings */
  completedCount: number;
}

export function useTaskSiblings(
  allTasks: TaskFieldsFragment[],
  currentTask: TaskFieldsFragment | null,
): TaskSiblingInfo {
  return useMemo(() => {
    if (!currentTask) {
      return { total: 0, position: 0, siblings: [], completedCount: 0 };
    }

    // TODO: workflowInstanceId was removed from TaskFieldsFragment (DRR-124 internal field).
    // Until a replacement grouping field is available, treat all tasks as siblings.
    const siblings = allTasks
      .filter(() => true)
      .sort((a, b) => {
        // Sort by sequenceOrder if available, else createdAt
        if (a.sequenceOrder != null && b.sequenceOrder != null) {
          return a.sequenceOrder - b.sequenceOrder;
        }
        return (a.createdAt ?? '').localeCompare(b.createdAt ?? '');
      });

    const position = siblings.findIndex(t => t.id === currentTask.id) + 1;
    const completedCount = siblings.filter(
      t => t.status === GqlTaskStatus.COMPLETED
    ).length;

    return { total: siblings.length, position, siblings, completedCount };
  }, [allTasks, currentTask]);
}
